import React, { useState, useEffect } from 'react';
import { collection, addDoc, updateDoc, deleteDoc, doc, onSnapshot } from 'firebase/firestore';
import { ref, onValue } from 'firebase/database';
import { db, realtimeDb } from '../firebase';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { 
  Crown, 
  Plus, 
  Search, 
  Filter, 
  Trophy, 
  Users, 
  Calendar,
  Clock,
  DollarSign,
  Star,
  Settings,
  Edit,
  Trash2,
  Play,
  Pause,
  Award,
  Target,
  Gift,
  Medal,
  BarChart3,
  X
} from 'lucide-react';

interface Tournament {
  id: string;
  name: string;
  description: string;
  type: 'single_elimination' | 'double_elimination' | 'round_robin' | 'swiss';
  status: 'upcoming' | 'registration' | 'ongoing' | 'completed' | 'cancelled';
  maxParticipants: number;
  currentParticipants: number;
  entryFee: number;
  prizePool: number;
  startDate: string;
  endDate?: string;
  registrationDeadline: string;
  createdBy: string; // userId
  createdByName: string; // displayName
  createdAt: string;
  rules: string;
  rewards: TournamentReward[];
}

interface TournamentReward {
  position: number;
  type: 'coins' | 'trophy' | 'title' | 'avatar';
  value: number | string;
  description: string;
}

interface Leaderboard {
  id: string;
  tournamentId: string;
  playerId: string;
  playerName: string;
  playerAvatar?: string;
  position: number;
  score: number;
  gamesPlayed: number;
  gamesWon: number;
  winPercentage: number;
  totalTime: number; // minutes
  lastUpdated: string;
}

interface Prize {
  id: string;
  name: string;
  type: 'coins' | 'trophy' | 'title' | 'avatar' | 'special';
  value: number | string;
  rarity: 'common' | 'rare' | 'epic' | 'legendary';
  description: string;
  imageUrl?: string;
  isActive: boolean;
  createdAt: string;
}

const Tournaments: React.FC = () => {
  const { currentUser } = useAuth();
  const [activeTab, setActiveTab] = useState<'tournaments' | 'leaderboards' | 'prizes'>('tournaments');
  const [tournaments, setTournaments] = useState<Tournament[]>([]);
  const [leaderboards, setLeaderboards] = useState<Leaderboard[]>([]);
  const [prizes, setPrizes] = useState<Prize[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showPrizeModal, setShowPrizeModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showEditPrizeModal, setShowEditPrizeModal] = useState(false);
  const [selectedTournament, setSelectedTournament] = useState<Tournament | null>(null);
  const [selectedPrize, setSelectedPrize] = useState<Prize | null>(null);
  const [loading, setLoading] = useState(false);
  
  // Form states
  const [tournamentForm, setTournamentForm] = useState({
    name: '',
    description: '',
    type: 'single_elimination' as Tournament['type'],
    maxParticipants: 32,
    entryFee: 0,
    startDate: '',
    registrationDeadline: '',
    rules: ''
  });

  const [prizeForm, setPrizeForm] = useState({
    name: '',
    type: 'coins' as Prize['type'],
    value: '',
    rarity: 'common' as Prize['rarity'],
    description: ''
  });

  // Hybrid Firebase loading: Firestore for admin data, Realtime DB for live game data
  useEffect(() => {
    // Load tournaments from Firestore (admin data)
    const unsubscribeTournaments = onSnapshot(
      collection(db, 'tournaments'), 
      (snapshot) => {
        const tournamentsData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Tournament[];
        setTournaments(tournamentsData);
      },
      (error) => {
        console.error('Error fetching tournaments:', error);
        if (error.code !== 'permission-denied') {
          toast.error('Hiba történt a versenyek betöltésekor');
        }
      }
    );

    // Load leaderboards from Realtime Database (live game data)
    const leaderboardsRef = ref(realtimeDb, 'leaderboards');
    const unsubscribeLeaderboards = onValue(
      leaderboardsRef, 
      (snapshot) => {
        if (snapshot.exists()) {
          const data = snapshot.val();
          const leaderboardsArray = Object.keys(data).map(key => ({
            id: key,
            ...data[key]
          })) as Leaderboard[];
          setLeaderboards(leaderboardsArray);
        } else {
          setLeaderboards([]);
        }
      },
      (error) => {
        console.error('Error fetching leaderboards:', error);
        setLeaderboards([]);
      }
    );

    // Load prizes from Firestore (admin data)
    const unsubscribePrizes = onSnapshot(
      collection(db, 'prizes'), 
      (snapshot) => {
        const prizesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        })) as Prize[];
        setPrizes(prizesData);
      },
      (error) => {
        console.error('Error fetching prizes:', error);
        if (error.code !== 'permission-denied') {
          toast.error('Hiba történt a díjak betöltésekor');
        }
      }
    );

    // Cleanup subscriptions
    return () => {
      try {
        unsubscribeTournaments();
        unsubscribeLeaderboards();
        unsubscribePrizes();
      } catch (error) {
        console.error('Error cleaning up tournament subscriptions:', error);
      }
    };
  }, []);

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'upcoming': return 'bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200';
      case 'registration': return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'ongoing': return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'completed': return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      case 'cancelled': return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRarityColor = (rarity: string) => {
    switch (rarity) {
      case 'common': return 'bg-gray-100 text-gray-800';
      case 'rare': return 'bg-blue-100 text-blue-800';
      case 'epic': return 'bg-purple-100 text-purple-800';
      case 'legendary': return 'bg-yellow-100 text-yellow-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString('hu-HU');
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    return hours > 0 ? `${hours}h ${mins}m` : `${mins}m`;
  };

  const handleCreateTournament = async () => {
    if (!tournamentForm.name.trim() || !tournamentForm.description.trim()) {
      toast.error('Név és leírás megadása kötelező');
      return;
    }

    setLoading(true);
    try {
      const newTournament: Omit<Tournament, 'id'> = {
        ...tournamentForm,
        status: 'upcoming',
        currentParticipants: 0,
        prizePool: tournamentForm.entryFee * tournamentForm.maxParticipants * 0.8, // 80% of total entry fees
        createdBy: currentUser?.uid || 'unknown',
        createdByName: currentUser?.displayName || currentUser?.email || 'Admin',
        createdAt: new Date().toISOString(),
        rewards: [
          { position: 1, type: 'coins', value: Math.floor(tournamentForm.entryFee * tournamentForm.maxParticipants * 0.5), description: '1. hely díja' },
          { position: 2, type: 'coins', value: Math.floor(tournamentForm.entryFee * tournamentForm.maxParticipants * 0.3), description: '2. hely díja' },
          { position: 3, type: 'coins', value: Math.floor(tournamentForm.entryFee * tournamentForm.maxParticipants * 0.2), description: '3. hely díja' }
        ]
      };

      await addDoc(collection(db, 'tournaments'), newTournament);
      
      // Reset form
      setTournamentForm({
        name: '',
        description: '',
        type: 'single_elimination',
        maxParticipants: 32,
        entryFee: 0,
        startDate: '',
        registrationDeadline: '',
        rules: ''
      });
      
      setShowCreateModal(false);
      toast.success('Verseny sikeresen létrehozva!');
    } catch (error) {
      console.error('Hiba a verseny létrehozásakor:', error);
      toast.error('Hiba történt a verseny létrehozásakor');
    } finally {
      setLoading(false);
    }
  };

  const handleCreatePrize = async () => {
    if (!prizeForm.name.trim() || !prizeForm.description.trim()) {
      toast.error('Név és leírás megadása kötelező');
      return;
    }

    setLoading(true);
    try {
      const newPrize: Omit<Prize, 'id'> = {
        ...prizeForm,
        value: prizeForm.type === 'coins' ? parseInt(prizeForm.value) : prizeForm.value,
        isActive: true,
        createdAt: new Date().toISOString()
      };

            await addDoc(collection(db, 'prizes'), newPrize);

      // Reset form
      setPrizeForm({
        name: '',
        type: 'coins',
        value: '',
        rarity: 'common',
        description: ''
      });
      
      setShowPrizeModal(false);
      toast.success('Díj sikeresen létrehozva!');
    } catch (error) {
      console.error('Hiba a díj létrehozásakor:', error);
      toast.error('Hiba történt a díj létrehozásakor');
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateTournamentStatus = async (tournamentId: string, newStatus: Tournament['status']) => {
    try {
      await updateDoc(doc(db, 'tournaments', tournamentId), {
        status: newStatus,
        updatedAt: new Date().toISOString()
      });
      toast.success('Verseny státusza frissítve!');
    } catch (error) {
      console.error('Hiba a státusz frissítésekor:', error);
      toast.error('Hiba történt a státusz frissítésekor');
    }
  };

  const handleDeletePrize = async (prizeId: string) => {
    if (window.confirm('Biztosan törölni szeretnéd ezt a díjat?')) {
      try {
        await deleteDoc(doc(db, 'prizes', prizeId));
        toast.success('Díj sikeresen törölve!');
      } catch (error) {
        console.error('Hiba a díj törlésekor:', error);
        toast.error('Hiba történt a díj törlésekor');
      }
    }
  };

  const handleEditTournament = (tournament: Tournament) => {
    setSelectedTournament(tournament);
    setTournamentForm({
      name: tournament.name,
      description: tournament.description,
      type: tournament.type,
      maxParticipants: tournament.maxParticipants,
      entryFee: tournament.entryFee,
      startDate: tournament.startDate,
      registrationDeadline: tournament.registrationDeadline,
      rules: tournament.rules
    });
    setShowEditModal(true);
  };

  const handleUpdateTournament = async () => {
    if (!selectedTournament || !tournamentForm.name.trim() || !tournamentForm.description.trim()) {
      toast.error('Név és leírás megadása kötelező');
      return;
    }

    setLoading(true);
    try {
      await updateDoc(doc(db, 'tournaments', selectedTournament.id), {
        ...tournamentForm,
        updatedAt: new Date().toISOString()
      });
      
      setShowEditModal(false);
      setSelectedTournament(null);
      setTournamentForm({
        name: '',
        description: '',
        type: 'single_elimination',
        maxParticipants: 32,
        entryFee: 0,
        startDate: '',
        registrationDeadline: '',
        rules: ''
      });
      toast.success('Verseny sikeresen frissítve!');
    } catch (error) {
      console.error('Hiba a verseny frissítésekor:', error);
      toast.error('Hiba történt a verseny frissítésekor');
    } finally {
      setLoading(false);
    }
  };

  const handleEditPrize = (prize: Prize) => {
    setSelectedPrize(prize);
    setPrizeForm({
      name: prize.name,
      type: prize.type,
      value: typeof prize.value === 'number' ? prize.value.toString() : prize.value,
      rarity: prize.rarity,
      description: prize.description
    });
    setShowEditPrizeModal(true);
  };

  const handleUpdatePrize = async () => {
    if (!selectedPrize || !prizeForm.name.trim() || !prizeForm.description.trim()) {
      toast.error('Név és leírás megadása kötelező');
      return;
    }

    setLoading(true);
    try {
      const updatedPrize = {
        ...prizeForm,
        value: prizeForm.type === 'coins' ? parseInt(prizeForm.value) : prizeForm.value,
        updatedAt: new Date().toISOString()
      };

      await updateDoc(doc(db, 'prizes', selectedPrize.id), updatedPrize);
      
      setShowEditPrizeModal(false);
      setSelectedPrize(null);
      setPrizeForm({
        name: '',
        type: 'coins',
        value: '',
        rarity: 'common',
        description: ''
      });
      toast.success('Díj sikeresen frissítve!');
    } catch (error) {
      console.error('Hiba a díj frissítésekor:', error);
      toast.error('Hiba történt a díj frissítésekor');
    } finally {
      setLoading(false);
    }
  };

  const ongoingTournaments = tournaments.filter(t => t.status === 'ongoing').length;
  const upcomingTournaments = tournaments.filter(t => t.status === 'upcoming' || t.status === 'registration').length;
  const totalParticipants = tournaments.reduce((sum, t) => sum + t.currentParticipants, 0);
  const totalPrizePool = tournaments.filter(t => t.status !== 'cancelled').reduce((sum, t) => sum + t.prizePool, 0);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 dark:text-white">
              Versenyek
            </h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              Tornák szervezése, leaderboard és díjazás kezelése
            </p>
          </div>
          <div className="mt-4 sm:mt-0 flex space-x-3">
            {activeTab === 'tournaments' && (
              <button
                onClick={() => setShowCreateModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Új Verseny</span>
              </button>
            )}
            {activeTab === 'prizes' && (
              <button
                onClick={() => setShowPrizeModal(true)}
                className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
              >
                <Plus className="h-4 w-4" />
                <span>Új Díj</span>
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Folyamatban</p>
              <p className="text-2xl font-bold text-green-600">{ongoingTournaments}</p>
            </div>
            <Play className="h-8 w-8 text-green-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Közelgő</p>
              <p className="text-2xl font-bold text-blue-600">{upcomingTournaments}</p>
            </div>
            <Calendar className="h-8 w-8 text-blue-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Résztvevők</p>
              <p className="text-2xl font-bold text-purple-600">{totalParticipants}</p>
            </div>
            <Users className="h-8 w-8 text-purple-500" />
          </div>
        </div>

        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm font-medium text-gray-600 dark:text-gray-400">Össz díjazás</p>
              <p className="text-2xl font-bold text-yellow-600">{totalPrizePool.toLocaleString()}</p>
            </div>
            <DollarSign className="h-8 w-8 text-yellow-500" />
          </div>
        </div>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <nav className="flex space-x-8">
          <button
            onClick={() => setActiveTab('tournaments')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'tournaments'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            Versenyek ({tournaments.length})
          </button>
          <button
            onClick={() => setActiveTab('leaderboards')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'leaderboards'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            Leaderboard
          </button>
          <button
            onClick={() => setActiveTab('prizes')}
            className={`py-2 px-1 border-b-2 font-medium text-sm ${
              activeTab === 'prizes'
                ? 'border-blue-500 text-blue-600 dark:text-blue-400'
                : 'border-transparent text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
            }`}
          >
            Díjak ({prizes.length})
          </button>
        </nav>
      </div>

      {/* Search and Filter */}
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6 mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
            <input
              type="text"
              placeholder="Keresés..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
            />
          </div>
          
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
          >
            <option value="all">Minden státusz</option>
            {activeTab === 'tournaments' && (
              <>
                <option value="upcoming">Közelgő</option>
                <option value="registration">Regisztráció</option>
                <option value="ongoing">Folyamatban</option>
                <option value="completed">Befejezett</option>
                <option value="cancelled">Törölt</option>
              </>
            )}
          </select>
        </div>
      </div>

      {/* Content based on active tab */}
      {activeTab === 'tournaments' && (
        <div className="space-y-6">
          {tournaments.filter(tournament => 
            statusFilter === 'all' || tournament.status === statusFilter
          ).map((tournament) => (
            <div key={tournament.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
              <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between">
                <div className="flex-1">
                  <div className="flex items-center space-x-3 mb-2">
                    <h3 className="text-xl font-bold text-gray-900 dark:text-white">
                      {tournament.name}
                    </h3>
                    <span className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(tournament.status)}`}>
                      {tournament.status === 'upcoming' ? 'Közelgő' :
                       tournament.status === 'registration' ? 'Regisztráció' :
                       tournament.status === 'ongoing' ? 'Folyamatban' :
                       tournament.status === 'completed' ? 'Befejezett' : 'Törölt'}
                    </span>
                  </div>
                  
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    {tournament.description}
                  </p>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
                    <div className="flex items-center space-x-2">
                      <Users className="h-5 w-5 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {tournament.currentParticipants}/{tournament.maxParticipants}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <DollarSign className="h-5 w-5 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {tournament.prizePool.toLocaleString()} érme
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Calendar className="h-5 w-5 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {formatDate(tournament.startDate)}
                      </span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Trophy className="h-5 w-5 text-gray-400" />
                      <span className="text-sm text-gray-600 dark:text-gray-400">
                        {tournament.type === 'single_elimination' ? 'Egyenes kieséses' :
                         tournament.type === 'double_elimination' ? 'Dupla kieséses' :
                         tournament.type === 'round_robin' ? 'Körmérkőzéses' : 'Swiss rendszer'}
                      </span>
                    </div>
                  </div>
                  
                  <div className="flex flex-wrap gap-2">
                    {tournament.rewards.slice(0, 3).map((reward, index) => (
                      <span key={index} className="px-2 py-1 bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200 text-xs rounded-full">
                        {reward.position}. hely: {typeof reward.value === 'number' ? `${reward.value.toLocaleString()} érme` : reward.value}
                      </span>
                    ))}
                  </div>
                </div>
                
                <div className="mt-4 lg:mt-0 lg:ml-6 flex space-x-2">
                  <button 
                    onClick={() => handleEditTournament(tournament)}
                    className="flex items-center space-x-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                  >
                    <Edit className="h-4 w-4" />
                    <span>Szerkesztés</span>
                  </button>
                  {tournament.status === 'upcoming' && (
                    <button 
                      onClick={() => handleUpdateTournamentStatus(tournament.id, 'registration')}
                      className="flex items-center space-x-1 px-3 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors"
                    >
                      <Play className="h-4 w-4" />
                      <span>Regisztráció</span>
                    </button>
                  )}
                  {tournament.status === 'registration' && (
                    <button 
                      onClick={() => handleUpdateTournamentStatus(tournament.id, 'ongoing')}
                      className="flex items-center space-x-1 px-3 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
                    >
                      <Play className="h-4 w-4" />
                      <span>Indítás</span>
                    </button>
                  )}
                  {tournament.status === 'ongoing' && (
                    <button 
                      onClick={() => handleUpdateTournamentStatus(tournament.id, 'completed')}
                      className="flex items-center space-x-1 px-3 py-2 bg-yellow-500 text-white rounded-lg hover:bg-yellow-600 transition-colors"
                    >
                      <Pause className="h-4 w-4" />
                      <span>Befejezés</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {activeTab === 'leaderboards' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-md overflow-hidden">
          <div className="p-6 border-b border-gray-200 dark:border-gray-700">
            <h3 className="text-lg font-medium text-gray-900 dark:text-white">
              Heti Kihívás #31 - Leaderboard
            </h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-700">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Helyezés
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Játékos
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Pontszám
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Játékok
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Győzelem %
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Játékidő
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                    Utolsó frissítés
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                {leaderboards.map((entry) => (
                  <tr key={entry.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        {entry.position <= 3 && (
                          <Medal className={`h-5 w-5 mr-2 ${
                            entry.position === 1 ? 'text-yellow-500' :
                            entry.position === 2 ? 'text-gray-400' :
                            'text-amber-600'
                          }`} />
                        )}
                        <span className="text-sm font-medium text-gray-900 dark:text-white">
                          #{entry.position}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <img
                          className="h-10 w-10 rounded-full object-cover"
                          src={entry.playerAvatar || 'https://via.placeholder.com/40'}
                          alt=""
                        />
                        <div className="ml-4">
                          <div className="text-sm font-medium text-gray-900 dark:text-white">
                            {entry.playerName}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-gray-900 dark:text-white">
                        {entry.score.toLocaleString()}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {entry.gamesWon}/{entry.gamesPlayed}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {entry.winPercentage.toFixed(1)}%
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDuration(entry.totalTime)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-500 dark:text-gray-400">
                        {formatDate(entry.lastUpdated)}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {activeTab === 'prizes' && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {prizes.map((prize) => (
            <div key={prize.id} className="bg-white dark:bg-gray-800 rounded-xl shadow-md p-6">
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center space-x-3">
                  <div className={`p-2 rounded-lg ${
                    prize.type === 'coins' ? 'bg-yellow-100 dark:bg-yellow-900' :
                    prize.type === 'trophy' ? 'bg-blue-100 dark:bg-blue-900' :
                    prize.type === 'title' ? 'bg-purple-100 dark:bg-purple-900' :
                    'bg-gray-100 dark:bg-gray-700'
                  }`}>
                    {prize.type === 'coins' ? <DollarSign className="h-6 w-6 text-yellow-600" /> :
                     prize.type === 'trophy' ? <Trophy className="h-6 w-6 text-blue-600" /> :
                     prize.type === 'title' ? <Award className="h-6 w-6 text-purple-600" /> :
                     <Gift className="h-6 w-6 text-gray-600" />}
                  </div>
                  <div>
                    <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                      {prize.name}
                    </h3>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${getRarityColor(prize.rarity)}`}>
                      {prize.rarity === 'common' ? 'Gyakori' :
                       prize.rarity === 'rare' ? 'Ritka' :
                       prize.rarity === 'epic' ? 'Epikus' : 'Legendás'}
                    </span>
                  </div>
                </div>
                <div className="flex space-x-1">
                  <button 
                    onClick={() => handleEditPrize(prize)}
                    className="p-1 text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                  >
                    <Edit className="h-4 w-4" />
                  </button>
                  <button 
                    onClick={() => handleDeletePrize(prize.id)}
                    className="p-1 text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              
              <p className="text-gray-600 dark:text-gray-400 text-sm mb-3">
                {prize.description}
              </p>
              
              <div className="flex items-center justify-between">
                <div className="text-lg font-bold text-gray-900 dark:text-white">
                  {typeof prize.value === 'number' ? `${prize.value.toLocaleString()} érme` : prize.value}
                </div>
                <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                  prize.isActive ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                }`}>
                  {prize.isActive ? 'Aktív' : 'Inaktív'}
                </span>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Tournament Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Új Verseny Létrehozása
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Verseny neve
                    </label>
                    <input
                      type="text"
                      value={tournamentForm.name}
                      onChange={(e) => setTournamentForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="pl. Nyári Bajnokság"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Típus
                    </label>
                    <select 
                      value={tournamentForm.type}
                      onChange={(e) => setTournamentForm(prev => ({ ...prev, type: e.target.value as Tournament['type'] }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      <option value="single_elimination">Egyenes kieséses</option>
                      <option value="double_elimination">Dupla kieséses</option>
                      <option value="round_robin">Körmérkőzéses</option>
                      <option value="swiss">Swiss rendszer</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Max résztvevők
                    </label>
                    <input
                      type="number"
                      value={tournamentForm.maxParticipants}
                      onChange={(e) => setTournamentForm(prev => ({ ...prev, maxParticipants: parseInt(e.target.value) || 32 }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="64"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nevezési díj (érme)
                    </label>
                    <input
                      type="number"
                      value={tournamentForm.entryFee}
                      onChange={(e) => setTournamentForm(prev => ({ ...prev, entryFee: parseInt(e.target.value) || 0 }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="1000"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Kezdés dátuma
                    </label>
                    <input
                      type="datetime-local"
                      value={tournamentForm.startDate}
                      onChange={(e) => setTournamentForm(prev => ({ ...prev, startDate: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Nevezés határideje
                    </label>
                    <input
                      type="datetime-local"
                      value={tournamentForm.registrationDeadline}
                      onChange={(e) => setTournamentForm(prev => ({ ...prev, registrationDeadline: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    />
                  </div>
                </div>
                
                <div className="mt-4">
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                    Leírás
                  </label>
                  <textarea
                    rows={3}
                    value={tournamentForm.description}
                    onChange={(e) => setTournamentForm(prev => ({ ...prev, description: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    placeholder="Verseny leírása..."
                  />
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleCreateTournament}
                  disabled={loading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Létrehozás...' : 'Verseny létrehozása'}
                </button>
                <button
                  onClick={() => setShowCreateModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Mégse
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Create Prize Modal */}
      {showPrizeModal && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white mb-4">
                  Új Díj Létrehozása
                </h3>
                
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Díj neve
                    </label>
                    <input
                      type="text"
                      value={prizeForm.name}
                      onChange={(e) => setPrizeForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="pl. Arany Trófea"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Típus
                    </label>
                    <select 
                      value={prizeForm.type}
                      onChange={(e) => setPrizeForm(prev => ({ ...prev, type: e.target.value as Prize['type'] }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      <option value="coins">Érmék</option>
                      <option value="trophy">Trófea</option>
                      <option value="title">Cím</option>
                      <option value="avatar">Avatar</option>
                      <option value="special">Speciális</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Ritkaság
                    </label>
                    <select 
                      value={prizeForm.rarity}
                      onChange={(e) => setPrizeForm(prev => ({ ...prev, rarity: e.target.value as Prize['rarity'] }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                    >
                      <option value="common">Gyakori</option>
                      <option value="rare">Ritka</option>
                      <option value="epic">Epikus</option>
                      <option value="legendary">Legendás</option>
                    </select>
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Érték
                    </label>
                    <input
                      type="text"
                      value={prizeForm.value}
                      onChange={(e) => setPrizeForm(prev => ({ ...prev, value: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="pl. 10000 vagy Bajnok"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                      Leírás
                    </label>
                    <textarea
                      rows={3}
                      value={prizeForm.description}
                      onChange={(e) => setPrizeForm(prev => ({ ...prev, description: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      placeholder="Díj leírása..."
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleCreatePrize}
                  disabled={loading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-green-600 text-base font-medium text-white hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {loading ? 'Létrehozás...' : 'Díj létrehozása'}
                </button>
                <button
                  onClick={() => setShowPrizeModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Mégse
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Tournament Modal */}
      {showEditModal && selectedTournament && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Verseny szerkesztése
                  </h3>
                  <button
                    onClick={() => setShowEditModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Név
                    </label>
                    <input
                      type="text"
                      value={tournamentForm.name}
                      onChange={(e) => setTournamentForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Verseny neve"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Leírás
                    </label>
                    <textarea
                      value={tournamentForm.description}
                      onChange={(e) => setTournamentForm(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Verseny leírása"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Típus
                      </label>
                      <select
                        value={tournamentForm.type}
                        onChange={(e) => setTournamentForm(prev => ({ ...prev, type: e.target.value as Tournament['type'] }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="single_elimination">Egyenes kieséses</option>
                        <option value="double_elimination">Dupla kieséses</option>
                        <option value="round_robin">Körmérkőzéses</option>
                        <option value="swiss">Svájci</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Max résztvevők
                      </label>
                      <input
                        type="number"
                        value={tournamentForm.maxParticipants}
                        onChange={(e) => setTournamentForm(prev => ({ ...prev, maxParticipants: parseInt(e.target.value) }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Belépési díj (coins)
                    </label>
                    <input
                      type="number"
                      value={tournamentForm.entryFee}
                      onChange={(e) => setTournamentForm(prev => ({ ...prev, entryFee: parseInt(e.target.value) }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Kezdés
                      </label>
                      <input
                        type="datetime-local"
                        value={tournamentForm.startDate}
                        onChange={(e) => setTournamentForm(prev => ({ ...prev, startDate: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Regisztráció vége
                      </label>
                      <input
                        type="datetime-local"
                        value={tournamentForm.registrationDeadline}
                        onChange={(e) => setTournamentForm(prev => ({ ...prev, registrationDeadline: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Szabályok
                    </label>
                    <textarea
                      value={tournamentForm.rules}
                      onChange={(e) => setTournamentForm(prev => ({ ...prev, rules: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Verseny szabályai"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleUpdateTournament}
                  disabled={loading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {loading ? 'Mentés...' : 'Mentés'}
                </button>
                <button
                  onClick={() => setShowEditModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Mégse
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Edit Prize Modal */}
      {showEditPrizeModal && selectedPrize && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            <div className="fixed inset-0 transition-opacity" aria-hidden="true">
              <div className="absolute inset-0 bg-gray-500 opacity-75"></div>
            </div>

            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>

            <div className="inline-block align-bottom bg-white dark:bg-gray-800 rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white dark:bg-gray-800 px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    Díj szerkesztése
                  </h3>
                  <button
                    onClick={() => setShowEditPrizeModal(false)}
                    className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                  >
                    <X className="h-6 w-6" />
                  </button>
                </div>

                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Név
                    </label>
                    <input
                      type="text"
                      value={prizeForm.name}
                      onChange={(e) => setPrizeForm(prev => ({ ...prev, name: e.target.value }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Díj neve"
                    />
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Típus
                      </label>
                      <select
                        value={prizeForm.type}
                        onChange={(e) => setPrizeForm(prev => ({ ...prev, type: e.target.value as Prize['type'] }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      >
                        <option value="coins">Érmék</option>
                        <option value="item">Tárgy</option>
                        <option value="badge">Jelvény</option>
                      </select>
                    </div>

                    <div>
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Érték
                      </label>
                      <input
                        type={prizeForm.type === 'coins' ? 'number' : 'text'}
                        value={prizeForm.value}
                        onChange={(e) => setPrizeForm(prev => ({ ...prev, value: e.target.value }))}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                        placeholder={prizeForm.type === 'coins' ? 'Mennyiség' : 'Tárgy neve'}
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Ritkaság
                    </label>
                    <select
                      value={prizeForm.rarity}
                      onChange={(e) => setPrizeForm(prev => ({ ...prev, rarity: e.target.value as Prize['rarity'] }))}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                    >
                      <option value="common">Gyakori</option>
                      <option value="rare">Ritka</option>
                      <option value="epic">Epikus</option>
                      <option value="legendary">Legendás</option>
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      Leírás
                    </label>
                    <textarea
                      value={prizeForm.description}
                      onChange={(e) => setPrizeForm(prev => ({ ...prev, description: e.target.value }))}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:text-white"
                      placeholder="Díj leírása"
                    />
                  </div>
                </div>
              </div>

              <div className="bg-gray-50 dark:bg-gray-700 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  onClick={handleUpdatePrize}
                  disabled={loading}
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50"
                >
                  {loading ? 'Mentés...' : 'Mentés'}
                </button>
                <button
                  onClick={() => setShowEditPrizeModal(false)}
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 dark:border-gray-600 shadow-sm px-4 py-2 bg-white dark:bg-gray-800 text-base font-medium text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                >
                  Mégse
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Tournaments; 